import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  message,
  Select,
  Space,
  Typography,
} from 'antd';
import axios from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import api from '@/services/api';
import { createSkillUploadApi } from '@/services/skillUploadApi';
import {
  createSkillUploadFlow,
  type SkillUploadPhase,
} from './skillUploadFlow';

interface UploadFields {
  itemKey: string;
  version: string;
  agentProviders: string[];
  nameZh?: string;
  nameEn?: string;
  descriptionZh?: string;
  descriptionEn?: string;
}
const phaseLabels: Record<SkillUploadPhase, string> = {
  hashing: '正在计算文件摘要…',
  signing: '正在准备上传…',
  uploading: '正在上传 ZIP…',
  publishing: '正在校验包并发布版本…',
};
function locales(zh?: string, en?: string): Record<string, string> {
  return {
    ...(zh?.trim() ? { 'zh-CN': zh.trim() } : {}),
    ...(en?.trim() ? { 'en-US': en.trim() } : {}),
  };
}
function failureMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const text = error.response?.data?.message;
    if (typeof text === 'string' && text.trim()) return text;
  }
  return error instanceof Error ? error.message : '上传发布失败，请重试';
}
export function SkillUploadDialog({
  onPublished,
}: {
  onPublished: () => void;
}) {
  const flow = useMemo(
    () => createSkillUploadFlow(createSkillUploadApi(api)),
    [],
  );
  const [form] = Form.useForm<UploadFields>();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File>();
  const [phase, setPhase] = useState<SkillUploadPhase>();
  const [error, setError] = useState('');
  const attempt = useRef(0);
  useEffect(
    () => () => {
      attempt.current += 1;
      flow.cancel();
    },
    [flow],
  );

  const close = () => {
    attempt.current += 1;
    flow.cancel();
    setOpen(false);
    setPhase(undefined);
    setError('');
    setFile(undefined);
    form.resetFields();
  };
  const publish = async () => {
    const current = ++attempt.current;
    let values: UploadFields;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (attempt.current !== current) return;
    if (!file) {
      setError('请选择 Skill ZIP 文件');
      return;
    }
    setError('');
    try {
      const result = await flow.run(
        {
          itemKey: values.itemKey.trim(),
          version: values.version.trim(),
          file,
          agentProviders: values.agentProviders,
          displayName: locales(values.nameZh, values.nameEn),
          displayDescription: locales(
            values.descriptionZh,
            values.descriptionEn,
          ),
        },
        setPhase,
      );
      if (attempt.current !== current) return;
      close();
      message.success(
        result.created
          ? 'Skill 版本已发布。请选择当前版本并配置上架市场。'
          : '已确认该版本发布成功。可继续配置上架市场。',
      );
      onPublished();
    } catch (cause) {
      if (attempt.current === current) setError(failureMessage(cause));
    } finally {
      if (attempt.current === current) setPhase(undefined);
    }
  };
  return (
    <>
      <Button type="primary" onClick={() => setOpen(true)}>
        上传 Skill 包
      </Button>
      <Modal
        title="上传并发布 Skill"
        open={open}
        width={640}
        destroyOnHidden
        okText={phase ? '发布中' : '上传并发布'}
        cancelText={phase ? '取消上传' : '取消'}
        confirmLoading={Boolean(phase)}
        onCancel={close}
        onOk={() => void publish()}
      >
        <Alert
          className="mb-4"
          type="info"
          showIcon
          message="ZIP 根目录需包含 SKILL.md"
          description="SKILL.md 的 frontmatter name 必须与 key 一致，并包含 description。references、assets 等资源可一同打包。ZIP 不超过 64 MiB；服务端会校验完整包，发布后再单独配置上架。"
        />
        {error ? (
          <Alert
            className="mb-4"
            type="error"
            showIcon
            message="尚未确认发布成功"
            description={error}
          />
        ) : null}
        {phase ? (
          <Alert
            className="mb-4"
            type="info"
            message={phaseLabels[phase]}
            description="关闭窗口会取消等待。若发布请求已送达，重新上传相同文件与版本可验证结果。"
          />
        ) : null}
        <Form<UploadFields>
          form={form}
          layout="vertical"
          disabled={Boolean(phase)}
          preserve={false}
          initialValues={{ version: '1.0.0', agentProviders: [] }}
        >
          <Space align="start" className="w-full">
            <Form.Item
              name="itemKey"
              label="Skill key"
              rules={[{ required: true, message: '请输入稳定 key' }]}
            >
              <Input placeholder="code-review" maxLength={64} />
            </Form.Item>
            <Form.Item
              name="version"
              label="版本"
              rules={[{ required: true, message: '请输入版本' }]}
            >
              <Input placeholder="1.0.0" />
            </Form.Item>
          </Space>
          <Form.Item label="Skill ZIP 文件" required>
            <Input
              aria-label="Skill ZIP 文件"
              type="file"
              accept=".zip,application/zip"
              onChange={(event) => setFile(event.target.files?.[0])}
            />
          </Form.Item>
          <Form.Item
            name="agentProviders"
            label="支持的 Agent"
            rules={[{ required: true, message: '请选择至少一个 Agent' }]}
          >
            <Select
              mode="multiple"
              placeholder="选择已验证支持此 Skill 的 Agent"
              options={[
                { value: 'codex', label: 'Codex' },
                { value: 'claude-code', label: 'Claude Code' },
                { value: 'tutti-agent', label: 'Tutti Agent' },
              ]}
            />
          </Form.Item>
          <Typography.Paragraph type="secondary">
            以下展示信息可选；留空时使用 SKILL.md 中的名称和描述。
          </Typography.Paragraph>
          <Space align="start" className="w-full">
            <Form.Item name="nameZh" label="中文名称">
              <Input />
            </Form.Item>
            <Form.Item name="nameEn" label="英文名称">
              <Input />
            </Form.Item>
          </Space>
          <Form.Item name="descriptionZh" label="中文描述">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="descriptionEn" label="英文描述">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
